export interface SqlConfig {
  name?: string;
  server: string;
  port: string;
  database: string;
  username: string;
  password: string;
  ssl?: boolean;
  sid?: string;
  tablePrefix?: string | undefined;
}

export interface ApiConfig {
  endpoint: string;
  authType: 'none' | 'apiKey' | 'bearer' | 'basic' | 'oauth2';
  apiKeyName?: string;
  apiKeyValue?: string;
  apiKeyLocation?: 'header' | 'query';
  token?: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  tokenUrl?: string;
  headers?: Record<string, string>;
}

export type IntegrationConfig = SqlConfig | ApiConfig;

export interface Integration {
  id: string;
  userId?: string;
  name: string;
  type: 'sql' | 'api';
  subType: string;
  status: 'active' | 'inactive' | 'error' | never;
  stats?: {
    queryCount?: number;

    avgQueryTime?: string;

    tableCount?: number;

    lastSyncDate?: string | Date;
  };
  schema_type: 'mermaid' | 'json' | 'other';
  schema_content: string;
  config: IntegrationConfig;
  lastConnected: Date | string | null;
  createdAt: Date | string;
  updatedAt?: Date | string;
  index?: number;
  analysis: string | undefined;
  summary: string;
  keywords: string;
  comment?: string | undefined;
  creatorName?: string;
}

// Yardımcı fonksiyonlar
export const getTypeName = (integration: Integration): string => {
  if (integration.type === 'sql') {
    switch (integration.subType) {
      case 'mysql':
        return 'MySQL';
      case 'postgresql':
        return 'PostgreSQL';
      case 'mssql':
        return 'MS SQL Server';
      default:
        return 'SQL Veritabanı';
    }
  } else if (integration.type === 'api') {
    switch (integration.subType) {
      case 'rest':
        return 'REST API';
      case 'soap':
        return 'SOAP API';
      case 'graphql':
        return 'GraphQL API';
      default:
        return 'API';
    }
  }
  return 'Bilinmeyen';
};

export const getStatusText = (status: string): string => {
  switch (status) {
    case 'active':
      return 'Aktif';
    case 'inactive':
      return 'Pasif';
    case 'error':
      return 'Hata';
    default:
      return 'Bilinmiyor';
  }
};

export const getStatusClass = (status: string): string => {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-100 px-2 py-0 rounded-full text-xs font-medium';
    case 'inactive':
      return 'text-gray-600 bg-gray-100 px-2 py-0 rounded-full text-xs font-medium';
    case 'error':
      return 'text-red-600 bg-red-100 px-2 py-0 rounded-full text-xs font-medium';
    default:
      return 'text-gray-600 bg-gray-100 px-2 py-0 rounded-full text-xs font-medium';
  }
};

export const getIconBackground = (type: string): string => {
  switch(type) {
    case 'sql':
      return 'bg-blue-50 text-blue-500';
    case 'api':
      return 'bg-purple-50 text-purple-500';
    default:
      return 'bg-gray-50 text-gray-500';
  }
};

export const getDefaultPort = (subType?: string): string => {
  switch (subType) {
    case 'mysql':
      return '3306';
    case 'postgresql':
      return '5432';
    case 'mssql':
      return '1433';
    case 'oracle':
      return '1521';
    default:
      return 'Varsayılan';
  }
};

export const getAuthType = (integration: Integration): string => {
  // Only check authType if it's an API integration
  if (integration.type === 'api') {
    // Access authType from the config property, not directly from integration
    const apiConfig = integration.config as ApiConfig;

    switch (apiConfig.authType) {
      case 'apiKey':
        return 'API Key';
      case 'bearer':
        return 'Bearer Token';
      case 'basic':
        return 'Basic Auth';
      case 'oauth2':
        return 'OAuth 2.0';
      case 'none':
        return 'Yok';
      default:
        return 'Yok';
    }
  }

  // For non-API integrations
  return 'Yok';
};
export const formatDate = (date: Date | string | null): string => {
  if (!date) return 'Hiç bağlanılmadı';

  // Eğer string ise Date objesine dönüştür
  const dateObj = date instanceof Date ? date : new Date(date);

  // Hatalı tarih kontrolü
  if (isNaN(dateObj.getTime())) {
    return 'Geçersiz tarih';
  }

  // Check if it's today
  const today = new Date();
  const isToday =
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear();

  if (isToday) {
    return `Bugün ${dateObj.getHours()}:${String(dateObj.getMinutes()).padStart(2, '0')}`;
  }

  // Check if it's yesterday
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    dateObj.getDate() === yesterday.getDate() &&
    dateObj.getMonth() === yesterday.getMonth() &&
    dateObj.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return `Dün ${dateObj.getHours()}:${String(dateObj.getMinutes()).padStart(2, '0')}`;
  }

  // Otherwise show full date
  return `${dateObj.getDate()}.${
    dateObj.getMonth() + 1
  }.${dateObj.getFullYear()} ${dateObj.getHours()}:${String(dateObj.getMinutes()).padStart(
    2,
    '0'
  )}`;
};

export interface CreateIntegrationInput {
  name: string;
  type: 'sql' | 'api';
  subType: string;
  config: object;
}

export interface UpdateIntegrationInput {
  id: string;
  name?: string;
  type?: 'sql' | 'api';
  subType?: string;
  config?: object;
  status?: string;
}
